import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { gql, useLazyQuery } from '@apollo/client';
import { OrderFiltersBlock } from './OrderFiltersBlock';
import { ButtonGroup } from './common/ButtonGroup';
// import { AgCharts } from 'ag-charts-react';
// import type { AgChartOptions } from 'ag-charts-community';
import * as countryCodes from 'country-codes-list';
import { getPrimaryColor } from 'language-flag-colors'
import { withScreen } from './Screen';
import { GetStatsQuery, GetStatsQueryVariables, OrdersFilters } from '../../codegen/graphql';
import { parseIntoURLSearchParams } from '../lib/parseIntoURLSearchParams';

// GraphQL query for stats
const GET_STATS = gql`
  query GetStats($search: String, $startDate: DateTimeISO, $endDate: DateTimeISO, $countryCode: String, $adId: String, $variationId: String) {
    stats(search: $search, startDate: $startDate, endDate: $endDate, countryCode: $countryCode, adId: $adId, variationId: $variationId) {
      distributionPerCountry {
        country
        count
        percentage
      }
    }
  }
`;

type StatsView = 'distribution' | 'timeline' | 'platform' | 'status';

const VIEW_OPTIONS = [
  { value: 'distribution' as StatsView, label: 'Distribution' },
  { value: 'timeline' as StatsView, label: 'Timeline' },
  { value: 'platform' as StatsView, label: 'Platform' },
  { value: 'status' as StatsView, label: 'Status' },
];

function Stats() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedView, setSelectedView] = useState<StatsView>('distribution');
  const [getStats, { data, loading, error }] = useLazyQuery<
    GetStatsQuery,
    GetStatsQueryVariables
  >(GET_STATS);

  // Filter states
  const currentFilters: OrdersFilters = useMemo(() => ({
    search: searchParams.get('search'),
    startDate: searchParams.get('startDate'),
    endDate: searchParams.get('endDate'),
    countryCode: searchParams.get('countryCode'),
    adId: searchParams.get('adId'),
    variationId: searchParams.get('variationId'),
  }), [searchParams]);

  const handleFiltersChange = (change: Partial<OrdersFilters>) => {
    setSearchParams(parseIntoURLSearchParams(change));
  };

  // Fetch stats when filters change
  useEffect(() => {
    getStats({ variables: currentFilters });
  }, [currentFilters]);

  // Chart options for distribution per country
  const getDistributionChartOptions = () => {
    if (!data?.stats?.distributionPerCountry) {
      return {
        title: { text: 'No data available' },
        series: []
      };
    }

    console.log(countryCodes.findOne('countryCode', 'FR'));
    return {
      title: { text: 'Orders Distribution by Country' },
      series: [
        {
          // @ts-ignore
          type: 'pie',
          data: data.stats.distributionPerCountry.map((item: any) => {
            const country = countryCodes.findOne('countryCode', item.country);
            return ({
              country: country?.flag + ' ' + country?.countryNameEn,
              countryWithCount: country?.flag + ' ' + country?.countryNameEn + ' (' + item.count + ')',
              count: item.count,
              percentage: item.percentage,
              primaryColor: getPrimaryColor(item.country)
            })
          }),
          angleKey: 'count',
          // labelKey: 'countryWithCount',
          calloutLabelKey: 'countryWithCount',
          // sectorLabelKey: 'countryWithCount',
          // radiusKey: 'percentage',
          itemStyler: (params: any) => {
            return {
              fill: params.datum.primaryColor.hex
            }
          }
        }
      ],
      legend: {
        enabled: false
      }
    };
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Statistics</h1>
        <p className="text-gray-600">Analyze your order data with interactive charts and filters</p>
      </div>

      {/* Filters */}
      <OrderFiltersBlock
        currentFilters={currentFilters}
        onFiltersChange={handleFiltersChange}
      />

      {/* View Selector */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Select View</h2>
        <ButtonGroup
          options={VIEW_OPTIONS}
          value={selectedView}
          onChange={setSelectedView}
        />
      </div>

      {/* Chart Area */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        {loading && (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        )}

        {error && (
          <div className="text-center text-red-600 p-8">
            <p>Error loading statistics: {error.message}</p>
          </div>
        )}

        {!loading && !error && (
          <>
            {/* {selectedView === 'distribution' && (
              <AgCharts
                options={getDistributionChartOptions()}
                style={{ height: 500 }}
              />
            )} */}
            
            {selectedView === 'timeline' && (
              <div className="flex items-center justify-center h-full text-gray-500">
                Timeline view coming soon...
              </div>
            )}
            
            {selectedView === 'platform' && (
              <div className="flex items-center justify-center h-full text-gray-500">
                Platform view coming soon...
              </div>
            )}
            
            {selectedView === 'status' && (
              <div className="flex items-center justify-center h-full text-gray-500">
                Status view coming soon...
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default withScreen(Stats); 